import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const Noticias = () => {
  const { user, API } = useContext(AuthContext);
  const [noticias, setNoticias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNoticia, setNewNoticia] = useState({
    titulo: '',
    contenido: '',
    categoria: 'estudiantes',
    imagenes: [],
    links: [],
    audio_url: null,
    youtube_url: ''
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [newLink, setNewLink] = useState({ titulo: '', url: '' });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [expandedNews, setExpandedNews] = useState({});
  
  // Edit states
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingNoticia, setEditingNoticia] = useState(null);
  const [editForm, setEditForm] = useState({
    titulo: '',
    contenido: '',
    categoria: 'estudiantes'
  });
  
  // Delete states
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingNoticia, setDeletingNoticia] = useState(null);

  useEffect(() => {
    loadNoticias();
  }, []);

  const loadNoticias = async () => {
    try {
      const response = await axios.get(`${API}/noticias`);
      setNoticias(response.data);
    } catch (error) {
      toast.error('Error al cargar noticias');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNoticia = async (e) => {
    e.preventDefault();
    if (!newNoticia.titulo || !newNoticia.contenido || !newNoticia.categoria) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/noticias`, newNoticia);
      toast.success('Noticia creada exitosamente');
      setNoticias([response.data, ...noticias]);
      setNewNoticia({
        titulo: '',
        contenido: '',
        categoria: 'estudiantes',
        imagenes: [],
        links: [],
        audio_url: null,
        youtube_url: ''
      });
      setNewLink({ titulo: '', url: '' });
      setIsDialogOpen(false);
    } catch (error) {
      toast.error('Error al crear noticia');
      console.error(error);
    } finally {
      setCreating(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten archivos de imagen');
      return;
    }

    if (file.size > 15 * 1024 * 1024) {
      toast.error('La imagen es demasiado grande (máximo 15MB)');
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload/news-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setNewNoticia(prev => ({
        ...prev,
        imagenes: [...prev.imagenes, response.data.image_url]
      }));

      toast.success('Imagen subida exitosamente');
    } catch (error) {
      toast.error('Error al subir imagen');
      console.error(error);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAudioUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['audio/mp3', 'audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/m4a'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Solo se permiten archivos de audio (MP3, WAV, OGG, M4A)');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast.error('El archivo de audio es demasiado grande (máximo 50MB)');
      return;
    }

    setUploadingAudio(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload/news-audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setNewNoticia(prev => ({
        ...prev,
        audio_url: response.data.audio_url
      }));

      toast.success('Audio subido exitosamente');
    } catch (error) {
      toast.error('Error al subir audio');
      console.error(error);
    } finally {
      setUploadingAudio(false);
    }
  };

  const addLink = () => {
    if (!newLink.titulo || !newLink.url) {
      toast.error('Completa título y URL del enlace');
      return;
    }

    // Simple URL validation
    try {
      new URL(newLink.url);
    } catch {
      toast.error('URL inválida');
      return;
    }

    setNewNoticia(prev => ({
      ...prev,
      links: [...prev.links, { ...newLink }]
    }));

    setNewLink({ titulo: '', url: '' });
  };

  const removeLink = (index) => {
    setNewNoticia(prev => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index)
    }));
  };

  const removeImage = (index) => {
    setNewNoticia(prev => ({
      ...prev,
      imagenes: prev.imagenes.filter((_, i) => i !== index)
    }));
  };

  const extractYouTubeId = (url) => {
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&\n?#]+)/);
    return match ? match[1] : null;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-UY', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleEditClick = (noticia) => {
    setEditingNoticia(noticia);
    setEditForm({
      titulo: noticia.titulo,
      contenido: noticia.contenido,
      categoria: noticia.categoria
    });
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      await axios.put(`${API}/noticias/${editingNoticia.id}`, editForm);
      toast.success('Noticia actualizada exitosamente');
      setIsEditDialogOpen(false);
      loadNoticias();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar noticia');
    }
  };

  const handleDeleteClick = (noticia) => {
    setDeletingNoticia(noticia);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`${API}/noticias/${deletingNoticia.id}`);
      toast.success('Noticia eliminada exitosamente');
      setIsDeleteDialogOpen(false);
      loadNoticias();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar noticia');
    }
  };

  const toggleExpanded = (noticiaId) => {
    setExpandedNews(prev => ({
      ...prev,
      [noticiaId]: !prev[noticiaId]
    }));
  };

  const truncateText = (text, maxLength = 150) => {
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="noticias-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Noticias del Instituto</h1>
          <p className="text-gray-600">Mantente al día con las últimas novedades y anuncios</p>
        </div>
        
        {(user.rol === 'admin' || user.rol === 'profesor') && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="mt-4 lg:mt-0 instituto-gradient btn-hover text-white" data-testid="create-noticia-button">
                <i className="fas fa-plus mr-2"></i>
                Publicar Noticia
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Publicar Nueva Noticia</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateNoticia} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="titulo">Título *</Label>
                  <Input
                    id="titulo"
                    value={newNoticia.titulo}
                    onChange={(e) => setNewNoticia({ ...newNoticia, titulo: e.target.value })}
                    placeholder="Título de la noticia"
                    required
                    data-testid="noticia-titulo-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="categoria">Categoría *</Label>
                  <Select value={newNoticia.categoria} onValueChange={(value) => setNewNoticia({ ...newNoticia, categoria: value })}>
                    <SelectTrigger data-testid="noticia-categoria-select">
                      <SelectValue placeholder="Selecciona la audiencia" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="publica">
                        <div className="flex items-center gap-2">
                          <i className="fas fa-globe text-green-600"></i>
                          Pública (visible en landing page sin login)
                        </div>
                      </SelectItem>
                      <SelectItem value="estudiantes">
                        <div className="flex items-center gap-2">
                          <i className="fas fa-user-graduate text-blue-600"></i>
                          Para Estudiantes y Pastores
                        </div>
                      </SelectItem>
                      <SelectItem value="profesores">
                        <div className="flex items-center gap-2">
                          <i className="fas fa-chalkboard-teacher text-purple-600"></i>
                          Para Profesores (solo visible por profesores)
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    {newNoticia.categoria === 'publica' 
                      ? '🌍 Esta noticia será visible públicamente en la página de inicio (sin necesidad de login)' 
                      : newNoticia.categoria === 'estudiantes' 
                        ? '👥 Esta noticia será vista por estudiantes y profesores' 
                        : '👨‍🏫 Esta noticia solo será vista por profesores y administradores'}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="contenido">Contenido *</Label>
                  <Textarea
                    id="contenido"
                    value={newNoticia.contenido}
                    onChange={(e) => setNewNoticia({ ...newNoticia, contenido: e.target.value })}
                    placeholder="Escribe aquí el contenido de la noticia..."
                    rows={8}
                    required
                    data-testid="noticia-contenido-input"
                  />
                </div>
                
                {/* Contenido Multimedia */}
                <div className="border-t pt-4 space-y-4">
                  <h4 className="font-medium text-gray-700 flex items-center">
                    <i className="fas fa-images mr-2"></i>
                    Contenido Multimedia (Opcional)
                  </h4>
                  
                  {/* Subida de Imágenes */}
                  <div className="space-y-3">
                    <Label className="text-base font-semibold">
                      <i className="fas fa-images mr-2 text-blue-600"></i>
                      Imágenes (Opcional)
                    </Label>
                    
                    {/* Preview de imágenes */}
                    {newNoticia.imagenes.length > 0 && (
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        {newNoticia.imagenes.map((imageUrl, index) => (
                          <div key={index} className="relative">
                            <img
                              src={imageUrl.startsWith('http') ? imageUrl : `${API}${imageUrl}`}
                              alt={`Preview ${index + 1}`}
                              className="w-full h-20 object-cover rounded border"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentNode.innerHTML = '<div class="w-full h-20 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-500">Error</div>';
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center hover:bg-red-600"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Opción 1: Subir desde PC */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700 mb-2 block">
                        <i className="fas fa-upload mr-2"></i>
                        Opción 1: Subir desde PC
                      </Label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          disabled={uploadingImage}
                          className="hidden"
                          id="image-upload"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => document.getElementById('image-upload').click()}
                          disabled={uploadingImage}
                          size="sm"
                        >
                          <i className={`fas ${uploadingImage ? 'fa-spinner fa-spin' : 'fa-image'} mr-2`}></i>
                          {uploadingImage ? 'Subiendo...' : 'Agregar Imagen'}
                        </Button>
                        <span className="text-xs text-gray-500">Máx. 15MB (JPG, PNG, GIF)</span>
                      </div>
                    </div>
                    
                    {/* Divisor */}
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white text-gray-500">O</span>
                      </div>
                    </div>
                    
                    {/* Opción 2: URL Externa */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700 mb-2 block">
                        <i className="fas fa-link mr-2"></i>
                        Opción 2: URL de Imagen Externa (Imgur, etc.)
                      </Label>
                      <div className="flex space-x-2">
                        <Input
                          type="url"
                          placeholder="https://i.imgur.com/ejemplo.jpg"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const url = e.target.value.trim();
                              if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
                                setNewNoticia(prev => ({
                                  ...prev,
                                  imagenes: [...prev.imagenes, url]
                                }));
                                e.target.value = '';
                                toast.success('URL de imagen agregada');
                              } else {
                                toast.error('Por favor ingresa una URL válida (debe empezar con http:// o https://)');
                              }
                            }
                          }}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            const input = e.target.closest('div').querySelector('input');
                            const url = input.value.trim();
                            if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
                              setNewNoticia(prev => ({
                                ...prev,
                                imagenes: [...prev.imagenes, url]
                              }));
                              input.value = '';
                              toast.success('URL de imagen agregada');
                            } else {
                              toast.error('Por favor ingresa una URL válida');
                            }
                          }}
                        >
                          <i className="fas fa-plus"></i>
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        <i className="fas fa-info-circle mr-1"></i>
                        Pega la URL y presiona Enter o click en +
                      </p>
                    </div>
                  </div>

                  {/* Enlaces */}
                  <div className="space-y-2">
                    <Label>Enlaces</Label>
                    <div className="flex space-x-2">
                      <Input
                        placeholder="Título del enlace"
                        value={newLink.titulo}
                        onChange={(e) => setNewLink({ ...newLink, titulo: e.target.value })}
                        className="flex-1"
                      />
                      <Input
                        placeholder="https://ejemplo.com"
                        value={newLink.url}
                        onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        onClick={addLink}
                        size="sm"
                        variant="outline"
                      >
                        <i className="fas fa-plus mr-1"></i>
                        Agregar
                      </Button>
                    </div>
                    
                    {/* Lista de enlaces */}
                    {newNoticia.links.length > 0 && (
                      <div className="space-y-1">
                        {newNoticia.links.map((link, index) => (
                          <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                            <div>
                              <span className="font-medium">{link.titulo}</span>
                              <span className="text-sm text-gray-500 ml-2">{link.url}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeLink(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <i className="fas fa-trash text-xs"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Audio */}
                  <div className="space-y-2">
                    <Label>Audio</Label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="file"
                        accept="audio/*"
                        onChange={handleAudioUpload}
                        disabled={uploadingAudio}
                        className="hidden"
                        id="audio-upload"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('audio-upload').click()}
                        disabled={uploadingAudio || newNoticia.audio_url}
                        size="sm"
                      >
                        <i className={`fas ${uploadingAudio ? 'fa-spinner fa-spin' : 'fa-microphone'} mr-2`}></i>
                        {uploadingAudio ? 'Subiendo...' : newNoticia.audio_url ? 'Audio subido' : 'Agregar Audio'}
                      </Button>
                      <span className="text-xs text-gray-500">Máx. 50MB (MP3, WAV, OGG, M4A)</span>
                    </div>
                    
                    {/* Preview de audio */}
                    {newNoticia.audio_url && (
                      <div className="flex items-center justify-between bg-blue-50 p-2 rounded">
                        <audio controls className="flex-1">
                          <source src={`${API}${newNoticia.audio_url}`} />
                          Tu navegador no soporta la reproducción de audio.
                        </audio>
                        <button
                          type="button"
                          onClick={() => setNewNoticia({ ...newNoticia, audio_url: null })}
                          className="ml-2 text-red-500 hover:text-red-700"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Video de YouTube */}
                  <div className="space-y-2">
                    <Label>Video de YouTube</Label>
                    <Input
                      placeholder="https://www.youtube.com/watch?v=..."
                      value={newNoticia.youtube_url}
                      onChange={(e) => setNewNoticia({ ...newNoticia, youtube_url: e.target.value })}
                    />
                    
                    {/* Preview de YouTube */}
                    {newNoticia.youtube_url && extractYouTubeId(newNoticia.youtube_url) && (
                      <div className="relative bg-gray-100 rounded overflow-hidden">
                        <iframe
                          width="100%"
                          height="200"
                          src={`https://www.youtube.com/embed/${extractYouTubeId(newNoticia.youtube_url)}`}
                          title="YouTube video preview"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    disabled={creating}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="instituto-gradient text-white"
                    disabled={creating}
                    data-testid="noticia-submit-button"
                  >
                    {creating ? (
                      <>Publicando... <i className="fas fa-spinner fa-spin ml-2"></i></>
                    ) : (
                      <>Publicar <i className="fas fa-bullhorn ml-2"></i></>
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* News List */}
      {noticias.length > 0 ? (
        <div className="space-y-6">
          {noticias.map((noticia) => {
            const isExpanded = expandedNews[noticia.id];
            const shouldShowToggle = noticia.contenido.length > 150;
            
            return (
              <Card key={noticia.id} className="shadow-lg border-0 card-hover overflow-hidden">
                <CardHeader className={`pb-3 ${
                noticia.categoria === 'publica'
                  ? 'bg-gradient-to-r from-green-50 to-emerald-50'
                  : noticia.categoria === 'estudiantes' 
                    ? 'bg-gradient-to-r from-blue-50 to-indigo-50' 
                    : 'bg-gradient-to-r from-purple-50 to-pink-50'
              } border-b`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-xl mb-2 text-gray-900 line-clamp-2">
                        {noticia.titulo}
                      </CardTitle>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <div className="flex items-center">
                          <i className="fas fa-user-edit mr-2 text-green-600"></i>
                          <span className="font-medium">{noticia.autor_nombre}</span>
                        </div>
                        <div className="flex items-center">
                          <i className="fas fa-calendar-alt mr-2 text-blue-600"></i>
                          <span>{formatDate(noticia.fecha_publicacion)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant="secondary" 
                        className={`flex-shrink-0 ${
                          noticia.categoria === 'publica'
                            ? 'bg-green-100 text-green-800'
                            : noticia.categoria === 'estudiantes' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-purple-100 text-purple-800'
                        }`}
                      >
                        <i className={`${
                          noticia.categoria === 'publica'
                            ? 'fas fa-globe'
                            : noticia.categoria === 'estudiantes' 
                              ? 'fas fa-user-graduate' 
                              : 'fas fa-chalkboard-teacher'
                        } mr-1`}></i>
                        {noticia.categoria === 'publica' 
                          ? '🌍 Pública' 
                          : noticia.categoria === 'estudiantes' 
                            ? 'Para Estudiantes' 
                            : 'Para Profesores'}
                      </Badge>
                      
                      {/* Edit/Delete buttons for admin or own news */}
                      {(user?.rol === 'admin' || noticia.autor_id === user?.id) && (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditClick(noticia)}
                            className="h-8 w-8 p-0"
                          >
                            <i className="fas fa-edit text-blue-600"></i>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(noticia)}
                            className="h-8 w-8 p-0"
                          >
                            <i className="fas fa-trash text-red-600"></i>
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="p-6">
                  <div className="prose max-w-none">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {isExpanded ? noticia.contenido : truncateText(noticia.contenido)}
                    </p>
                    
                    {shouldShowToggle && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleExpanded(noticia.id)}
                        className="mt-3 p-0 h-auto text-blue-600 hover:text-blue-800 hover:bg-transparent"
                        data-testid={`toggle-noticia-${noticia.id}`}
                      >
                        {isExpanded ? (
                          <>Ver menos <i className="fas fa-chevron-up ml-1"></i></>
                        ) : (
                          <>Ver más <i className="fas fa-chevron-down ml-1"></i></>
                        )}
                      </Button>
                    )}
                  </div>
                  
                  {/* Contenido Multimedia */}
                  {(noticia.imagenes?.length > 0 || noticia.audio_url || noticia.youtube_url || noticia.links?.length > 0) && (
                    <div className="mt-4 space-y-4">
                      
                      {/* Imágenes */}
                      {noticia.imagenes?.length > 0 && (
                        <div className="space-y-2">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                            {noticia.imagenes.map((imageUrl, index) => (
                              <img
                                key={index}
                                src={imageUrl.startsWith('http') ? imageUrl : `${API}${imageUrl}`}
                                alt={`Imagen ${index + 1}`}
                                className="w-full h-48 object-cover rounded-lg border shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                                onClick={() => {
                                  const fullUrl = imageUrl.startsWith('http') ? imageUrl : `${API}${imageUrl}`;
                                  window.open(fullUrl, '_blank');
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Audio */}
                      {noticia.audio_url && (
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex items-center mb-2">
                            <i className="fas fa-volume-up text-blue-600 mr-2"></i>
                            <span className="font-medium text-gray-700">Audio adjunto</span>
                          </div>
                          <audio controls className="w-full">
                            <source src={`${API}${noticia.audio_url}`} />
                            Tu navegador no soporta la reproducción de audio.
                          </audio>
                        </div>
                      )}
                      
                      {/* Video de YouTube */}
                      {noticia.youtube_url && extractYouTubeId(noticia.youtube_url) && (
                        <div className="relative bg-gray-100 rounded-lg overflow-hidden">
                          <iframe
                            width="100%"
                            height="300"
                            src={`https://www.youtube.com/embed/${extractYouTubeId(noticia.youtube_url)}`}
                            title="Video de YouTube"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="rounded-lg"
                          ></iframe>
                        </div>
                      )}
                      
                      {/* Enlaces */}
                      {noticia.links?.length > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center mb-2">
                            <i className="fas fa-link text-blue-600 mr-2"></i>
                            <span className="font-medium text-gray-700">Enlaces relacionados</span>
                          </div>
                          {noticia.links.map((link, index) => (
                            <a
                              key={index}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
                            >
                              <i className="fas fa-external-link-alt text-blue-600 mr-3"></i>
                              <div>
                                <div className="font-medium text-blue-800">{link.titulo}</div>
                                <div className="text-sm text-blue-600">{link.url}</div>
                              </div>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Engagement Actions */}
                  <div className="mt-6 pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <span className="text-sm text-gray-500">
                          <i className="fas fa-eye mr-1"></i>
                          Noticia importante
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                          data-testid={`share-noticia-${noticia.id}`}
                        >
                          <i className="fas fa-share-alt mr-1"></i>
                          Compartir
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="shadow-lg border-0">
          <CardContent className="text-center py-12">
            <i className="fas fa-newspaper text-gray-300 text-6xl mb-6"></i>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay noticias publicadas</h3>
            <p className="text-gray-600 mb-6">
              {['estudiante', 'pastor', 'graduado'].includes(user.rol)
                ? 'No hay noticias disponibles en este momento. Revisa más tarde.'
                : 'Comienza publicando la primera noticia para mantener informada a la comunidad del instituto.'}
            </p>
            {(user.rol === 'admin' || user.rol === 'profesor') && (
              <Button
                onClick={() => setIsDialogOpen(true)}
                className="instituto-gradient text-white btn-hover"
              >
                <i className="fas fa-plus mr-2"></i>
                Publicar primera noticia
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* News Guidelines */}
      {(user.rol === 'admin' || user.rol === 'profesor') && (
        <Card className="shadow-md border-0">
          <CardContent className="p-4">
            <div className="flex items-start space-x-3">
              <i className="fas fa-lightbulb text-yellow-500 mt-1"></i>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Consejos para Noticias Efectivas</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Usa títulos claros y descriptivos</li>
                  <li>• Incluye fecha y hora de eventos importantes</li>
                  <li>• Mantén el contenido relevante y actualizado</li>
                  <li>• Considera el impacto en estudiantes y profesores</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Noticia</DialogTitle>
            <DialogDescription>
              Modifica la información de la noticia
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-titulo">Título *</Label>
              <Input
                id="edit-titulo"
                value={editForm.titulo}
                onChange={(e) => setEditForm({...editForm, titulo: e.target.value})}
                placeholder="Título de la noticia"
              />
            </div>
            <div>
              <Label htmlFor="edit-contenido">Contenido *</Label>
              <textarea
                id="edit-contenido"
                value={editForm.contenido}
                onChange={(e) => setEditForm({...editForm, contenido: e.target.value})}
                placeholder="Contenido de la noticia..."
                className="w-full min-h-[200px] p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <Label htmlFor="edit-categoria">Categoría *</Label>
              <Select 
                value={editForm.categoria} 
                onValueChange={(value) => setEditForm({...editForm, categoria: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona una categoría" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="publica">
                    <i className="fas fa-globe mr-2"></i>
                    🌍 Pública
                  </SelectItem>
                  <SelectItem value="estudiantes">
                    <i className="fas fa-user-graduate mr-2"></i>
                    Para Estudiantes
                  </SelectItem>
                  <SelectItem value="profesores">
                    <i className="fas fa-chalkboard-teacher mr-2"></i>
                    Para Profesores
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleEditSubmit} className="bg-blue-600 hover:bg-blue-700">
              <i className="fas fa-save mr-2"></i>
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Eliminación</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que deseas eliminar esta noticia?
            </DialogDescription>
          </DialogHeader>
          {deletingNoticia && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <p><strong>Título:</strong> {deletingNoticia.titulo}</p>
              <p className="text-sm text-gray-600 mt-2">
                {deletingNoticia.contenido.substring(0, 100)}...
              </p>
            </div>
          )}
          <p className="text-red-600 text-sm">
            <i className="fas fa-exclamation-triangle mr-1"></i>
            Esta acción no se puede deshacer.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              <i className="fas fa-trash mr-2"></i>
              Eliminar Noticia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Noticias;